// Логика и настройки голосового ввода: без импортов, чтобы юнит-тест на node:test
// не тянул i18n с import.meta.glob. Сетевой вызов живёт в agent-api.ts.

/** Потолок надиктовки. Дальше запись останавливается сама. */
export const VOICE_DICTATION_MAX_SECONDS = 120
/** Короче — это промах по кнопке, а не фраза. */
export const VOICE_DICTATION_MIN_SECONDS = 0.5

/**
 * Режим кнопки надиктовки. Вкл (по умолчанию) — push-to-talk: пишем, пока кнопку держат,
 * и отправляем на распознавание по отпусканию. Выкл — клик включает запись, второй клик
 * останавливает. Преф глобальный (на браузер), как и выбор микрофона.
 *
 * Сравниваем с "0", а не с "1": пустое хранилище должно читаться как «включено».
 */
const HOLD_TO_RECORD_STORAGE_KEY = "voice-dictation-hold"

const holdToRecordListeners = new Set<() => void>()
let holdToRecordSnapshot: boolean | undefined

function emitHoldToRecordPref(): void {
  for (const listener of holdToRecordListeners) listener()
}

function readStoredHoldToRecordPref(): boolean {
  if (typeof window === "undefined") return true
  return window.localStorage.getItem(HOLD_TO_RECORD_STORAGE_KEY) !== "0"
}

export function subscribeHoldToRecordPref(listener: () => void): () => void {
  holdToRecordListeners.add(listener)
  return () => {
    holdToRecordListeners.delete(listener)
  }
}

export function getHoldToRecordPrefSnapshot(): boolean {
  if (holdToRecordSnapshot === undefined) holdToRecordSnapshot = readStoredHoldToRecordPref()
  return holdToRecordSnapshot
}

export function readHoldToRecordPref(): boolean {
  return getHoldToRecordPrefSnapshot()
}

export function writeHoldToRecordPref(enabled: boolean): void {
  if (holdToRecordSnapshot === enabled) return
  holdToRecordSnapshot = enabled
  if (typeof window !== "undefined") {
    window.localStorage.setItem(HOLD_TO_RECORD_STORAGE_KEY, enabled ? "1" : "0")
  }
  emitHoldToRecordPref()
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== HOLD_TO_RECORD_STORAGE_KEY) return
    const next = event.newValue !== "0"
    if (holdToRecordSnapshot === next) return
    holdToRecordSnapshot = next
    emitHoldToRecordPref()
  })
}

/** Фолбэк клиентского предела ожидания распознавания, если shared prop не пришёл (тесты). */
export const VOICE_TRANSCRIBE_FALLBACK_TIMEOUT_MS = 60_000

/**
 * Клиентский предел ожидания POST /transcribe. Сервер отдаёт свой потолок таймаута STT
 * shared-пропом voice_transcribe_timeout_ms (STT_TRANSCRIBE_MAX_SEC + запас): браузер обязан
 * сдаваться позже Rails, иначе бэкенд дождётся провайдера, забиллит расшифровку, а текст
 * уйдёт в закрытый сокет. Мусор в пропе сводим к фолбэку.
 */
export function resolveTranscribeTimeoutMs(raw: unknown): number {
  const value = typeof raw === "number" ? raw : Number(raw)
  return Number.isFinite(value) && value >= 15_000 ? value : VOICE_TRANSCRIBE_FALLBACK_TIMEOUT_MS
}

export class VoiceTranscribeError extends Error {
  errorCode?: string

  constructor(message: string, errorCode?: string) {
    super(message)
    this.name = "VoiceTranscribeError"
    this.errorCode = errorCode
  }
}

/** Литералы, а не string: ключи локалей у t() типизированы строго. */
export type DictationErrorKey =
  | "voiceDictation.errorUnsupported"
  | "voiceDictation.errorTooLarge"
  | "voiceDictation.errorNoSpeech"
  | "voiceDictation.errorInsufficientFunds"
  | "voiceDictation.errorCompanyArchived"
  | "voiceDictation.errorUnavailable"
  | "voiceDictation.errorRateLimited"
  | "voiceDictation.errorFailed"

/**
 * Ключ строки локали по коду ошибки бэкенда. Неизвестный код (или его отсутствие —
 * например, при обрыве сети) сводим к общему сообщению.
 */
export function dictationErrorKey(errorCode: string | undefined): DictationErrorKey {
  switch (errorCode) {
    case "audio_missing":
    case "unsupported_type":
      return "voiceDictation.errorUnsupported"
    case "too_large":
      return "voiceDictation.errorTooLarge"
    case "empty":
      return "voiceDictation.errorNoSpeech"
    case "insufficient_funds":
      return "voiceDictation.errorInsufficientFunds"
    case "company_archived":
      return "voiceDictation.errorCompanyArchived"
    case "unavailable":
      return "voiceDictation.errorUnavailable"
    case "rate_limited":
      return "voiceDictation.errorRateLimited"
    default:
      return "voiceDictation.errorFailed"
  }
}
